/**
 * Pure tooltip-placement math for the guided tour overlay.
 * Viewport-relative coordinates throughout (suitable for position: fixed).
 */

import type { TourPlacement } from "./tours";

export interface Rect {
  top: number;
  left: number;
  width: number;
  height: number;
}

export interface Size {
  width: number;
  height: number;
}

export interface Viewport {
  width: number;
  height: number;
}

/** Gap between the spotlight edge and the tooltip card. */
export const TOOLTIP_GAP = 16;
/** Minimum distance from the viewport edge. */
export const VIEWPORT_MARGIN = 12;

type Side = Exclude<TourPlacement, "auto">;

function fits(side: Side, anchor: Rect, card: Size, viewport: Viewport): boolean {
  switch (side) {
    case "top":
      return anchor.top - TOOLTIP_GAP - card.height >= VIEWPORT_MARGIN;
    case "bottom":
      return anchor.top + anchor.height + TOOLTIP_GAP + card.height <= viewport.height - VIEWPORT_MARGIN;
    case "left":
      return anchor.left - TOOLTIP_GAP - card.width >= VIEWPORT_MARGIN;
    case "right":
      return anchor.left + anchor.width + TOOLTIP_GAP + card.width <= viewport.width - VIEWPORT_MARGIN;
  }
}

const OPPOSITE: Record<Side, Side> = {
  top: "bottom",
  bottom: "top",
  left: "right",
  right: "left",
};

function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), Math.max(min, max));
}

/**
 * Compute the tooltip card's fixed position for an anchor rect.
 * Tries the preferred side, then its opposite, then the remaining sides;
 * if nothing fits it uses the preferred side clamped into the viewport.
 */
export function placeTooltip(
  anchor: Rect,
  card: Size,
  viewport: Viewport,
  placement: TourPlacement = "auto"
): { top: number; left: number; side: Side } {
  const preferred: Side = placement === "auto" ? "bottom" : placement;
  const candidates: Side[] = [
    preferred,
    OPPOSITE[preferred],
    ...( ["bottom", "top", "right", "left"] as Side[]).filter(
      (s) => s !== preferred && s !== OPPOSITE[preferred]
    ),
  ];
  const side = candidates.find((s) => fits(s, anchor, card, viewport)) ?? preferred;

  const centeredLeft = anchor.left + anchor.width / 2 - card.width / 2;
  const centeredTop = anchor.top + anchor.height / 2 - card.height / 2;

  let top: number;
  let left: number;
  switch (side) {
    case "top":
      top = anchor.top - TOOLTIP_GAP - card.height;
      left = centeredLeft;
      break;
    case "bottom":
      top = anchor.top + anchor.height + TOOLTIP_GAP;
      left = centeredLeft;
      break;
    case "left":
      top = centeredTop;
      left = anchor.left - TOOLTIP_GAP - card.width;
      break;
    case "right":
      top = centeredTop;
      left = anchor.left + anchor.width + TOOLTIP_GAP;
      break;
  }

  return {
    top: clamp(top, VIEWPORT_MARGIN, viewport.height - card.height - VIEWPORT_MARGIN),
    left: clamp(left, VIEWPORT_MARGIN, viewport.width - card.width - VIEWPORT_MARGIN),
    side,
  };
}

/** Centered position for steps without an anchor (or the seek-timeout fallback). */
export function placeCentered(card: Size, viewport: Viewport): { top: number; left: number } {
  return {
    top: Math.max(VIEWPORT_MARGIN, (viewport.height - card.height) / 2),
    left: Math.max(VIEWPORT_MARGIN, (viewport.width - card.width) / 2),
  };
}
